import { AlertTriangle, CheckCircle } from 'lucide-react'
import { Banner, Button, Drawer } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { useLegacyImport } from './useLegacyImport'

function formatExpiry(expiresAt: string | undefined): string {
  if (!expiresAt) return ''
  return new Date(expiresAt).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

export function LegacyImportModal() {
  const {
    isVisible,
    step,
    produtos,
    expiresAt,
    selections,
    executeResult,
    isExecuting,
    setStep,
    togglePrice,
    handleExecute,
    handleRemindLater,
    handleClose,
  } = useLegacyImport()

  const selectedCount = Object.values(selections).filter(Boolean).length
  const importaveis = produtos.filter((p) => p.importavel)

  function onContinue() {
    if (selectedCount === 0) {
      toast.error('Selecione ao menos um preço para importar.')
      return
    }
    setStep('warnings')
  }

  return (
    <>
      <Drawer
        open={isVisible && step === 'selection'}
        onClose={handleRemindLater}
        title="Importar produtos do VendePay"
        description={expiresAt ? `Disponível até ${formatExpiry(expiresAt)}` : undefined}
        size="wide"
        variant="form"
        dismissible={false}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <Button type="button" variant="text" onClick={handleRemindLater}>
              Lembrar mais tarde
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onContinue}
              disabled={selectedCount === 0}
            >
              Continuar com {selectedCount} {selectedCount === 1 ? 'preço' : 'preços'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Banner tone="info">
            Cada preço selecionado cria um <strong>Rascunho</strong> separado. Revise e publique
            quando estiver pronto.
          </Banner>

          {importaveis.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {importaveis.map((produto) => (
                <div
                  key={produto.legacyId}
                  style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 16 }}
                >
                  <p style={{ fontWeight: 600, margin: '0 0 4px' }}>{produto.nome}</p>
                  {produto.descricao && (
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#6b7280' }}>
                      {produto.descricao}
                    </p>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {produto.precos.map((preco) => {
                      const key = `${produto.legacyId}|${preco.moeda}`
                      const isChecked = preco.jaImportada || selections[key] === true
                      return (
                        <label
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: preco.jaImportada ? 'default' : 'pointer',
                            opacity: preco.jaImportada ? 0.6 : 1,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={preco.jaImportada}
                            onChange={() => !preco.jaImportada && togglePrice(key)}
                            style={{ flexShrink: 0 }}
                          />
                          <span style={{ fontSize: 14 }}>
                            {parseFloat(preco.valor).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                            })}{' '}
                            <strong>{preco.moeda}</strong>
                            {preco.moeda !== 'BRL' && !preco.jaImportada && (
                              <span style={{ color: '#6b7280', marginLeft: 6, fontSize: 12 }}>
                                (será convertido para R$)
                              </span>
                            )}
                            {preco.jaImportada && (
                              <span style={{ color: '#10b981', marginLeft: 6, fontSize: 12 }}>
                                <CheckCircle
                                  size={12}
                                  style={{
                                    display: 'inline',
                                    verticalAlign: 'middle',
                                    marginRight: 3,
                                  }}
                                />
                                já importado
                              </span>
                            )}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {produtos.filter((p) => !p.importavel).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ fontWeight: 600, margin: 0, color: '#9ca3af', fontSize: 13 }}>
                Sem preço definido (não importáveis)
              </p>
              {produtos
                .filter((p) => !p.importavel)
                .map((p) => (
                  <p key={p.legacyId} style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>
                    {p.nome}
                  </p>
                ))}
            </div>
          )}
        </div>
      </Drawer>

      <Drawer
        open={isVisible && step === 'warnings'}
        onClose={() => setStep('selection')}
        title="Antes de confirmar"
        size="standard"
        variant="form"
        busy={isExecuting}
        onBack={() => setStep('selection')}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep('selection')}
              disabled={isExecuting}
            >
              Voltar
            </Button>
            <Button type="button" variant="primary" onClick={handleExecute} disabled={isExecuting}>
              {isExecuting
                ? 'Importando...'
                : `Importar ${selectedCount} ${selectedCount === 1 ? 'produto' : 'produtos'}`}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Banner tone="warning" icon={<AlertTriangle size={18} />}>
            Os links de checkout serão <strong>novos</strong>. Atualize campanhas e anúncios.
          </Banner>
          <Banner tone="warning" icon={<AlertTriangle size={18} />}>
            Scripts de upsell precisarão ser <strong>reconfigurados</strong> nos novos checkouts.
          </Banner>
          <Banner tone="warning" icon={<AlertTriangle size={18} />}>
            Relações de <strong>coprodução e afiliação</strong> precisarão ser recriadas
            manualmente.
          </Banner>
          <Banner tone="info">
            Produtos importados ficam como <strong>Rascunho</strong>. Você publica quando estiver
            pronto.
          </Banner>
        </div>
      </Drawer>

      <Drawer
        open={step === 'result' && !!executeResult}
        onClose={handleClose}
        title="Importação concluída"
        size="standard"
        variant="detail"
        footer={
          <Button type="button" variant="primary" onClick={handleClose}>
            Fechar
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Banner tone="success" icon={<CheckCircle size={18} />}>
            <strong>{executeResult?.importados ?? 0}</strong>{' '}
            {(executeResult?.importados ?? 0) === 1 ? 'produto importado' : 'produtos importados'}{' '}
            como Rascunho.
          </Banner>
          {(executeResult?.pendentes?.length ?? 0) > 0 && (
            <div>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Precisam de atenção manual:</p>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {executeResult?.pendentes.map((nome) => (
                  <li key={nome} style={{ fontSize: 14, color: '#6b7280' }}>
                    {nome}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Drawer>
    </>
  )
}
